import React, { useState } from 'react';
import styled from 'styled-components';
import { Footer } from '../Footer';

const Container = styled.div`
  min-height: 100vh;
  background-color: #F5F5F7;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  color: #1D1D1F;
`;

const Header = styled.div`
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 2rem;
  text-align: center;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.08);
`;

const Title = styled.h1`
  margin: 0;
  font-size: 2.5rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #1a73e8;
`;

const Subtitle = styled.p`
  margin: 0.5rem 0 0 0;
  font-size: 1.1rem;
  color: #86868B;
`;

const VideoSection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem;
`;

const VideoContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const VideoPlayer = styled.video`
  width: 100%;
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const VideoInfo = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
`;

const VideoTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: #1D1D1F;
  font-size: 1.2rem;
`;

const VideoDescription = styled.p`
  margin: 0;
  color: #86868B;
  line-height: 1.6;
`;

const NoVideoMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #86868B;
  
  h3 {
    margin: 0 0 1rem 0;
    color: #1D1D1F;
  }
  
  p {
    margin: 0.5rem 0;
    line-height: 1.6;
  }
  
  code {
    background-color: #f1f3f4;
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    color: #1a73e8;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fce8e6;
  color: #d93025;
  border: 1px solid rgba(217, 48, 37, 0.2);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  
  h4 {
    margin: 0 0 0.5rem 0;
    color: #d93025;
  }
  
  ul {
    margin: 0.5rem 0 0 1rem;
    padding: 0;
  }
  
  li {
    margin: 0.25rem 0;
  }
`;

const DemoVideo = () => {
  const [videoError, setVideoError] = useState(false);
  
  // Path to the demo video - you can change this to match your video file
  const videoPath = '/demo-video.mp4';
  
  // Video metadata - you can customize this
  const videoInfo = {
    title: 'Demo video',
    description: 'Here are some webapps we have been working on for the past two months.',
  };

  const handleVideoError = (e) => {
    console.log('Video failed to load:', e);
    setVideoError(true);
  };

  return (
    <Container>
      <Header>
        <Title>Demo Video</Title>
        <Subtitle>Watch our product demonstration</Subtitle>
      </Header>

      <VideoSection>
        {videoError && (
          <VideoContainer>
            <ErrorMessage>
              <h4>⚠️ Video Failed to Load</h4>
              <p>Your video file couldn't be loaded. Common issues:</p>
              <ul>
                <li><strong>File too large:</strong> Videos over 50MB may fail to load. Current file is 283MB.</li>
                <li><strong>Wrong location:</strong> Make sure the file is in <code>public/demo-video.mp4</code></li>
                <li><strong>Unsupported format:</strong> Use MP4 with H.264 codec for best compatibility</li>
                <li><strong>Network timeout:</strong> Large files may timeout on slower connections</li>
              </ul>
              <p>
                <strong>Solution:</strong> Compress your video to under 25MB using HandBrake or online tools. 
                See <code>public/video-compression-guide.txt</code> for detailed instructions.
              </p>
            </ErrorMessage>
          </VideoContainer>
        )}

        <VideoContainer>
          <VideoPlayer 
            controls 
            preload="metadata"
            src={videoPath}
            onError={handleVideoError}
          >
            Your browser does not support the video tag.
          </VideoPlayer>
          
          <VideoInfo>
            <VideoTitle>{videoInfo.title}</VideoTitle>
            <VideoDescription>{videoInfo.description}</VideoDescription>
          </VideoInfo>
        </VideoContainer>

        {/* Instructions for adding video */}
        <VideoContainer>
          <NoVideoMessage>
            <h3>📹 How to Add Your Demo Video</h3>
            <p>
              To display your demo video, simply place your video file in the <code>public</code> folder 
              and name it <code>demo-video.mp4</code> (or update the filename in the component).
            </p>
            <p>
              <strong>⚠️ Important:</strong> Keep video files under 25MB for best web performance. 
              Large files (like your current 283MB file) will fail to load.
            </p>
            <p>
              <strong>Supported formats:</strong> MP4 (H.264), WebM, MOV
            </p>
            <p>
              <strong>File location:</strong> <code>public/demo-video.mp4</code>
            </p>
            <p>
              <strong>Compression needed:</strong> See <code>public/video-compression-guide.txt</code> for help reducing file size.
            </p>
          </NoVideoMessage>
        </VideoContainer>
      </VideoSection>

      <Footer />
    </Container>
  );
};

export default DemoVideo; 